import React from 'react';
import PropTypes from 'prop-types';

import { AdyenCheckout, Donation } from '@adyen/adyen-web';
import styles from './AdyenDonation.module.scss';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const AdyenDonation = ({
    isPaymentSuccess,
    locale='',
    lyriaUrl,
    paymentRequestId,
    donationEnabled,
    shouldRedirect,
    setShouldRedirect,
    displayDonation,
    setDisplayDonation,
    setDonationSuccess,
    setDonationFinished,
}) => {
    const { t } = useTranslation();

    const [donationConfig, setDonationConfig] = React.useState({});
    const donationComponentRef = React.useRef(null);

    // if the locale provided in the token is for example: 'fr', need to format it to 'fr-FR' so Adyen recognise it
    // this works for current supported language de-DE, es-ES, fr-FR, it-IT, nl-NL, pt-PT.
    // english is Adyen's default language so we don't need to care about it.
    // full list of adyen-web supported languages: https://github.com/Adyen/adyen-web/tree/main/packages/server/translations
    const localeAdyenFormat = locale && !locale.includes('-') ? `${locale}-${locale.toUpperCase()}` : locale;

    const getDonationConfig = React.useCallback(() => {
        const url = `${lyriaUrl}/payments/donationConfig/${paymentRequestId}/`;
        axios({
            method: 'GET',
            url: url,
        }).then((response) => {
            if (response.data?.donated) {
                // If for some reason user reload page after already made a donation
                // -> not display Donation component anymore and redirect user 
                setShouldRedirect(true);
                setDonationSuccess(true);
                setDonationFinished(true);
            } else {
                setDonationConfig(response.data);
                // If the donation config is empty
                // -> feature flag to refuse non-card donation method is enabled
                // -> we dont show donation for this case
                // TODO: remove this after we are sure non-card donation works
                if (Object.keys(response.data).length === 0)  {
                    setShouldRedirect(true);
                    setDisplayDonation(false);
                    // setDonationSuccess(true);
                }
            }
        });
    }, [lyriaUrl, paymentRequestId, setDisplayDonation, setDonationSuccess, setShouldRedirect, setDonationFinished]);

    React.useEffect(() => {
        getDonationConfig();
    }, [donationEnabled, getDonationConfig, lyriaUrl, paymentRequestId, isPaymentSuccess]);

    const makeDonation = React.useCallback((data) => {
        const url = `${lyriaUrl}/payments/donation/${paymentRequestId}/`;
        axios({
            method: 'POST',
            url: url,
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            data: data,
        }).then((response) => {
            const status = response.data?.status;
            // completed and pending will be removed, kept here for retro-compatibility during deployment
            if (['completed', 'V', 'pending', 'P'].includes(status)) {
                setShouldRedirect(true);
                setDonationSuccess(true);
                setDonationFinished(true);
            // refund will be removed, kept here for retro-compatibility during deployment
            } else if (['refused', 'A'].includes(status)) {
                setShouldRedirect(true);
                setDonationSuccess(false);
                setDonationFinished(true);
            }
        }).catch((error) => {
            console.error('DONATE ERROR:', error);
            setShouldRedirect(true);
            setDonationSuccess(false);
            setDonationFinished(true);
        });
    }, [lyriaUrl, paymentRequestId, setDonationSuccess, setShouldRedirect, setDonationFinished]);

    const handleOnDonate = React.useCallback((state) => {
        makeDonation({
            'psp_payload': {
                ...state.data,
                donationCampaignId: donationConfig?.psp_config?.donationCampaigns[0].id,
            },
        });
    }, [donationConfig?.psp_config?.donationCampaigns, makeDonation]);

    const handleOnCancel = React.useCallback(() => {
        setDisplayDonation(false);
        setShouldRedirect(true);
    }, [setDisplayDonation, setShouldRedirect]);


    const adyenDonationConfig = React.useMemo(
        () => {
            let data = {
                ...donationConfig?.psp_config?.donationCampaigns[0],
                showCancelButton: true,
                onDonate: handleOnDonate,
                onCancel: handleOnCancel,
            };
            return data;
        }, [donationConfig, handleOnDonate, handleOnCancel]);


    React.useEffect(() => {
        // Component shouldn't be mounted if donationConfig is empty
        if (donationEnabled && donationConfig) {
            const mountDonation = async () => {
                const checkout = await AdyenCheckout(
                    {
                        clientKey: 'OKOK',
                        countryCode: 'FR',
                        environment: 'test',
                        locale: localeAdyenFormat,
                    });
                donationComponentRef.current = new Donation(checkout, adyenDonationConfig);
                donationComponentRef.current.mount('#donation-container');
            };
            mountDonation();
        }

        if (!displayDonation && donationComponentRef.current) {
            donationComponentRef.current.unmount();
        }

    }, [adyenDonationConfig, displayDonation, donationConfig, donationEnabled, localeAdyenFormat]);

    return (
        <>
            {donationEnabled && !shouldRedirect && donationConfig &&
                <div className={styles.donationTitle}>{t('addDonation')}</div>
            }
            <div className={styles.donationContainer}>
                <div id='donation-container'></div>
            </div>
        </>
    );

};

AdyenDonation.propTypes = {
    lyriaUrl: PropTypes.string,
    paymentRequestId: PropTypes.string,
    donationEnabled: PropTypes.bool,
    shouldRedirect: PropTypes.bool,
    displayDonation: PropTypes.bool,
};

export default AdyenDonation;
